import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { parseEmployerNote, noteTagLabelKey } from '../../utils/noteTags';
import CompareOverlay from './CompareOverlay';
import CompareCards, { ChipList, LineList, Pill } from './CompareCards';

const AMBER = '#F5A623';
const GREEN = '#1D9E75';
const NAVY  = '#0D1B2A';

// Same tier rule as interview transcription (employer_allows_transcription(): plan_tier in
// standard/plus, never Basic). Read off the employer row the dashboard already holds rather than
// that RPC, whose name is transcription-specific. This only gates the view -- everything it
// shows is applicant data the employer can already open one candidate at a time.
export const COMPARE_TIERS = ['standard', 'plus'];
export const employerCanCompare = employer => COMPARE_TIERS.includes(employer?.plan_tier);

// Interviews have no "completed" state -- an accepted one in the past is one that was held.
function interviewSummary(iv, t, lang) {
  if (!iv) return null;
  const when = iv.interview_at
    ? new Date(iv.interview_at).toLocaleString(lang, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  const isPast = iv.interview_at && new Date(iv.interview_at) < new Date();
  const key = iv.status === 'accepted' ? (isPast ? 'held' : 'confirmed')
    : iv.status === 'pending' ? 'requested'
    : iv.status === 'declined' ? 'declined'
    : iv.status === 'cancelled' ? 'cancelled'
    : null;
  const label = key ? t(`compare.employer.interview.${key}`) : iv.status;
  const color = key === 'confirmed' || key === 'held' ? GREEN : key === 'requested' ? AMBER : '#9CA3AF';
  return (
    <div>
      <Pill color={color}>{label}</Pill>
      {when && <div style={{ marginTop: '4px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{when}</div>}
    </div>
  );
}

export default function EmployerCandidateCompare({ employer, jobTitle, candidates, statusLabel, onRemove, onClear, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const allowed = employerCanCompare(employer);
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const workerKey = candidates.map(c => c.worker_id).sort().join(',');
  useEffect(() => {
    if (!allowed || candidates.length === 0) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const workerIds = [...new Set(candidates.map(c => c.worker_id))];
      const [trades, certs, licenses, experience, interviews] = await Promise.all([
        supabase.from('worker_trades').select('worker_id, trade, years_experience, min_hourly_rate, target_hourly_rate').in('worker_id', workerIds),
        supabase.from('worker_certifications').select('worker_id, certification_name, verification_status').in('worker_id', workerIds),
        supabase.from('worker_licenses').select('worker_id, license_name, state').in('worker_id', workerIds),
        supabase.from('worker_experience').select('worker_id, role, company, start_year, end_year, current').in('worker_id', workerIds).order('start_year', { ascending: false }),
        supabase.from('interviews').select('worker_id, job_id, interview_at, status').eq('employer_id', employer.id).in('worker_id', workerIds).order('interview_at', { ascending: false }),
      ]);
      if (cancelled) return;
      const by = (rows, w) => (rows.data || []).filter(r => r.worker_id === w);
      const next = {};
      candidates.forEach(c => {
        const jobTrade = (c.job?.trade_category || '').toLowerCase();
        const workerTrades = by(trades, c.worker_id);
        const workerIvs = by(interviews, c.worker_id);
        next[c.application_id] = {
          // The trade row matching this posting when there is one, otherwise their first.
          trade: workerTrades.find(r => (r.trade || '').toLowerCase() === jobTrade) || workerTrades[0] || null,
          certs: by(certs, c.worker_id),
          licenses: by(licenses, c.worker_id),
          experience: by(experience, c.worker_id),
          // This posting's interview first; otherwise their most recent one with this employer.
          interview: workerIvs.find(iv => iv.job_id === c.job_id) || workerIvs[0] || null,
        };
      });
      setDetails(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [allowed, workerKey, employer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = candidates.map(c => ({
    id: c.application_id,
    title: `${c.worker?.first_name || ''} ${c.worker?.last_name || ''}`.trim() || t('compare.unnamed'),
    subtitle: statusLabel(c),
    candidate: c,
  }));
  const d = item => details[item.id] || {};

  const rows = [
    {
      key: 'trade', label: t('compare.employer.rows.tradeExperience'),
      render: item => {
        const tr = d(item).trade;
        const exp = d(item).experience || [];
        if (!tr && exp.length === 0) return null;
        return (
          <LineList values={[
            tr && [tr.trade, tr.years_experience].filter(Boolean).join(' · '),
            ...exp.slice(0, 3).map(e => `${[e.role, e.company].filter(Boolean).join(t('compare.atSeparator'))} (${e.start_year || '?'}–${e.current ? t('compare.present') : (e.end_year || '?')})`),
          ].filter(Boolean)} />
        );
      },
    },
    {
      key: 'certs', label: t('compare.employer.rows.certifications'),
      render: item => <ChipList values={(d(item).certs || []).map(c => `${c.certification_name}${c.verification_status === 'verified' ? ' ✓' : ''}`)} />,
    },
    {
      key: 'licenses', label: t('compare.employer.rows.licenses'),
      render: item => <ChipList values={(d(item).licenses || []).map(l => [l.license_name, l.state].filter(Boolean).join(' · '))} />,
    },
    {
      key: 'pay', label: t('compare.employer.rows.askingPay'),
      render: item => {
        const tr = d(item).trade;
        if (!tr?.min_hourly_rate && !tr?.target_hourly_rate) return null;
        return (
          <LineList values={[
            tr.min_hourly_rate && t('compare.employer.minRate', { rate: tr.min_hourly_rate }),
            tr.target_hourly_rate && t('compare.employer.targetRate', { rate: tr.target_hourly_rate }),
          ].filter(Boolean)} />
        );
      },
    },
    {
      key: 'interview', label: t('compare.employer.rows.interview'),
      render: item => interviewSummary(d(item).interview, t, i18n.language) || t('compare.employer.interview.none'),
    },
    {
      key: 'notes', label: t('compare.employer.rows.staffNotes'),
      render: item => {
        const note = parseEmployerNote(item.candidate.employer_notes || '');
        if (!note.text && note.tags.length === 0) return null;
        return (
          <div>
            {note.tags.length > 0 && (
              <div style={{ marginBottom: note.text ? '6px' : 0 }}>
                <ChipList values={note.tags.map(tag => { const k = noteTagLabelKey(tag); return k ? t(k) : tag; })} />
              </div>
            )}
            {note.text && <div style={{ whiteSpace: 'pre-wrap' }}>{note.text}</div>}
            {note.authorName && (
              <div style={{ marginTop: '4px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                {t('employer.dashboard.candidates.noteLastEditedBy', { name: note.authorName })}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <CompareOverlay
      eyebrow={t('compare.employer.eyebrow', { jobTitle })}
      title={t('compare.employer.title', { count: candidates.length })}
      onClose={onClose}
      onClear={allowed && candidates.length > 0 ? onClear : null}
      clearLabel={t('common.clearAll')}
      closeLabel={t('compare.closeAriaLabel')}
    >
      {allowed ? (
        <CompareCards items={items} rows={rows} loading={loading} onRemove={onRemove} />
      ) : (
        <div data-testid="compare-upgrade-prompt" style={{
          maxWidth: '520px', margin: '2rem auto 0', textAlign: 'center',
          backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.35)',
          borderRadius: '12px', padding: '24px 20px',
        }}>
          <p style={{ fontSize: '1rem', fontWeight: 700, color: '#FFFFFF', marginBottom: '8px' }}>{t('compare.employer.upgradeTitle')}</p>
          <p style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: '16px' }}>{t('compare.employer.upgradeBody')}</p>
          <button
            onClick={() => navigate('/employer/pricing')}
            style={{ background: AMBER, color: NAVY, border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer' }}
          >
            {t('employer.postJob.viewPricingButton')}
          </button>
        </div>
      )}
    </CompareOverlay>
  );
}
